import "./security.css";
import {
  CheckCircleIcon,
  DesktopIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
  KeyIcon,
  ShieldIcon,
  SignOutIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useState, type FormEvent, type Ref } from "react";
import type { PasswordChangeInput } from "../types";
import {
  authenticationActionError,
  relativeSessionActivity,
} from "./presentation";

export function PasswordChangeForm({
  initialFeedback = "",
  onChangePassword,
  sectionRef,
}: {
  initialFeedback?: string;
  onChangePassword: (input: PasswordChangeInput) => Promise<void>;
  sectionRef?: Ref<HTMLElement>;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [feedback, setFeedback] = useState(initialFeedback);
  const [feedbackTone, setFeedbackTone] = useState<"error" | "success">(
    initialFeedback ? "success" : "error",
  );
  const [saving, setSaving] = useState(false);
  const feedbackId = "password-change-feedback";

  function clearFeedback(): void {
    if (feedback) setFeedback("");
  }

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (newPassword.length < 8) {
      setFeedbackTone("error");
      setFeedback("Use at least 8 characters.");
      return;
    }
    if (newPassword.length > 256) {
      setFeedbackTone("error");
      setFeedback("Use no more than 256 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setFeedbackTone("error");
      setFeedback("New passwords do not match.");
      return;
    }
    setSaving(true);
    setFeedback("");
    try {
      await onChangePassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setFeedbackTone("success");
      setFeedback("Password changed. Other browser sessions are signed out.");
    } catch (cause) {
      setFeedbackTone("error");
      setFeedback(
        cause instanceof Error
          ? cause.message
          : "Your password could not be changed.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="pm-passport-card scroll-mt-6" ref={sectionRef}>
      <header>
        <div>
          <h2>Account security</h2>
          <p>Use a memorable passphrase to protect every saved puzzle night.</p>
        </div>
        <small>Fresh sign-in required</small>
      </header>
      <form
        className="grid gap-5 p-6"
        onSubmit={(event) => void changePassword(event)}
      >
        <div className="grid gap-4 lg:grid-cols-3">
          <label className="grid gap-2 text-sm font-semibold text-white">
            <span>CURRENT PASSWORD</span>
            <input
              aria-describedby={feedback ? feedbackId : undefined}
              aria-invalid={
                feedbackTone === "error" && feedback ? true : undefined
              }
              autoComplete="current-password"
              className="min-h-11 rounded-xl border border-[var(--pm-border)] bg-[var(--pm-bg-canvas)] px-4 text-base text-white outline-none focus:border-[var(--pm-action-live)]"
              disabled={saving}
              maxLength={256}
              onChange={(event) => {
                setCurrentPassword(event.currentTarget.value);
                clearFeedback();
              }}
              required
              type="password"
              value={currentPassword}
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold text-white">
            <span>NEW PASSPHRASE</span>
            <input
              aria-describedby={feedback ? feedbackId : undefined}
              aria-invalid={
                feedbackTone === "error" && feedback ? true : undefined
              }
              autoComplete="new-password"
              className="min-h-11 rounded-xl border border-[var(--pm-border)] bg-[var(--pm-bg-canvas)] px-4 text-base text-white outline-none focus:border-[var(--pm-action-live)]"
              disabled={saving}
              maxLength={256}
              minLength={8}
              onChange={(event) => {
                setNewPassword(event.currentTarget.value);
                clearFeedback();
              }}
              required
              type="password"
              value={newPassword}
            />
          </label>
          <label className="grid gap-2 text-sm font-semibold text-white">
            <span>CONFIRM PASSPHRASE</span>
            <input
              aria-describedby={feedback ? feedbackId : undefined}
              aria-invalid={
                feedbackTone === "error" && feedback ? true : undefined
              }
              autoComplete="new-password"
              className="min-h-11 rounded-xl border border-[var(--pm-border)] bg-[var(--pm-bg-canvas)] px-4 text-base text-white outline-none focus:border-[var(--pm-action-live)]"
              disabled={saving}
              maxLength={256}
              minLength={8}
              onChange={(event) => {
                setConfirmPassword(event.currentTarget.value);
                clearFeedback();
              }}
              required
              type="password"
              value={confirmPassword}
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[var(--pm-border)] pt-5">
          <p className="max-w-2xl text-sm text-[var(--pm-text-secondary)]">
            8–256 characters. Spaces are welcome; no arbitrary symbol or number
            rules.
          </p>
          <button
            className="min-h-11 rounded-full bg-[#c9ff4a] px-6 py-3 font-bold text-[#07110d] transition hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c9ff4a] disabled:cursor-wait disabled:opacity-60"
            disabled={saving}
            type="submit"
          >
            {saving ? "Changing password…" : "Change password"}
          </button>
        </div>
        {feedback ? (
          <p
            aria-live="polite"
            className={
              feedbackTone === "error" ? "text-[#FFB8BE]" : "text-[#36d6e7]"
            }
            id={feedbackId}
            role={feedbackTone === "error" ? "alert" : "status"}
          >
            {feedback}
          </p>
        ) : null}
      </form>
    </section>
  );
}
